import React, { useEffect, useRef, useState } from 'react';
import api, { mediaUrl } from '../lib/api';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Pin, EyeOff, Eye, Trash2, Edit, X, Save, Bell, Image as ImageIcon, Crown, Sparkles, Loader2, ZoomIn } from 'lucide-react';

const SERVERS = [
  { id: 'prime', label: 'Server Prime', dot: 'bg-[var(--gold)]', icon: Crown, color: 'text-[var(--gold)]' },
  { id: 'elite', label: 'Server Elite', dot: 'bg-blue-500', icon: Sparkles, color: 'text-blue-400' },
];

function fmt(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit' }) + ' ' +
      d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
}

export default function AdminContents({ settings, onSettingsSaved }) {
  const [server, setServer] = useState('prime');
  const [items, setItems] = useState([]);
  const [showEditor, setShowEditor] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [previewItem, setPreviewItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [ttlDays, setTtlDays] = useState(settings?.contents_ttl_days || '60');
  const token = localStorage.getItem('gtc_token');

  const load = async () => {
    try {
      const { data } = await api.get('/contents', { params: { server } });
      setItems(data);
    } catch { toast.error('Errore caricamento'); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [server]);
  useEffect(() => { setTtlDays(settings?.contents_ttl_days || '60'); }, [settings]);

  const openNew = () => { setEditing(null); setShowEditor(true); };
  const openEdit = (item) => { setEditing(item); setShowEditor(true); };

  const togglePin = async (item) => {
    try { await api.put(`/contents/${item.id}`, { is_pinned: !item.is_pinned }); load(); }
    catch { toast.error('Errore'); }
  };
  const toggleHide = async (item) => {
    try { await api.put(`/contents/${item.id}`, { is_hidden: !item.is_hidden }); load(); }
    catch { toast.error('Errore'); }
  };
  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/contents/${deleteTarget.id}`);
      toast.success('Eliminato');
      setDeleteTarget(null);
      load();
    } catch { toast.error('Errore'); }
  };

  const saveTtl = async (val) => {
    setTtlDays(val);
    try {
      await api.put('/settings', { updates: { contents_ttl_days: String(val) } });
      toast.success('Impostazione salvata');
      onSettingsSaved?.();
    } catch { toast.error('Errore'); }
  };

  return (
    <div className="space-y-4">
      {/* Server switch */}
      <div className="flex gap-2">
        {SERVERS.map((s) => (
          <button
            key={s.id}
            data-testid={`admin-contents-server-${s.id}`}
            onClick={() => setServer(s.id)}
            className={`flex-1 px-4 py-2.5 rounded-2xl text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
              server === s.id
                ? 'bg-[var(--bg-elevated)] border border-[var(--gold)]/40 text-white'
                : 'bg-[var(--bg-surface)] border border-[var(--border-subtle)] text-white/60'
            }`}
          >
            <span className={`w-2 h-2 rounded-full ${s.dot}`} />
            {s.label}
          </button>
        ))}
      </div>

      {/* New post button */}
      <button
        data-testid="admin-new-content-btn"
        onClick={openNew}
        className="btn-gold w-full py-3 flex items-center justify-center gap-2"
      >
        <Plus className="w-4 h-4" /> Nuova pubblicazione
      </button>

      {/* TTL setting */}
      <div className="card-surface p-4">
        <div className="text-xs uppercase tracking-widest text-white/50 mb-2">Eliminazione automatica</div>
        <div className="flex flex-wrap gap-2">
          {[
            { v: '15', l: '15 giorni' },
            { v: '30', l: '30 giorni' },
            { v: '60', l: '60 giorni' },
            { v: '90', l: '90 giorni' },
            { v: '0', l: 'Mai' },
          ].map((o) => (
            <button
              key={o.v}
              data-testid={`admin-ttl-${o.v}`}
              onClick={() => saveTtl(o.v)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                String(ttlDays) === o.v
                  ? 'bg-[var(--gold)] text-black'
                  : 'bg-[var(--bg-surface)] border border-[var(--border-subtle)] text-white/70'
              }`}
            >
              {o.l}
            </button>
          ))}
        </div>
      </div>

      {/* Posts list */}
      <div className="space-y-3">
        {items.length === 0 && <div className="text-center text-white/40 text-sm py-8">Nessuna pubblicazione.</div>}
        {items.map((c) => (
          <div key={c.id} className={`card-surface p-3 flex gap-3 ${c.is_hidden ? 'opacity-50' : ''}`} data-testid={`admin-content-${c.id}`}>
            <button
              type="button"
              onClick={() => setPreviewItem(c)}
              data-testid={`admin-content-preview-${c.id}`}
              className="flex gap-3 flex-1 min-w-0 text-left"
            >
            {c.image_url ? (
              <div className="relative flex-shrink-0">
                <img src={mediaUrl(c.image_url)} alt="" className="w-16 h-16 rounded-xl object-cover" />
                <div className="absolute inset-0 rounded-xl bg-black/0 hover:bg-black/40 flex items-center justify-center transition-colors">
                  <ZoomIn className="w-4 h-4 text-white opacity-0 hover:opacity-100 transition-opacity" />
                </div>
              </div>
            ) : (
              <div className="w-16 h-16 rounded-xl bg-[var(--bg-elevated)] border border-[var(--border-subtle)] flex items-center justify-center flex-shrink-0">
                <ImageIcon className="w-5 h-5 text-white/30" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1 mb-1">
                {c.is_pinned && <Pin className="w-3 h-3 text-[var(--gold)]" />}
                {c.is_hidden && <EyeOff className="w-3 h-3 text-white/40" />}
                <span className="text-[10px] text-white/40">{fmt(c.created_at)}</span>
              </div>
              <div className="text-xs text-white/80 line-clamp-2 whitespace-pre-wrap break-words">{c.text}</div>
            </div>
            </button>
            <div className="flex flex-col gap-1 flex-shrink-0 justify-end">
              <div className="flex gap-1">
                <button data-testid={`content-edit-${c.id}`} onClick={() => openEdit(c)} className="p-1.5 text-white/60 hover:text-[var(--gold)]"><Edit className="w-3.5 h-3.5" /></button>
                <button data-testid={`content-pin-${c.id}`} onClick={() => togglePin(c)} className={`p-1.5 ${c.is_pinned ? 'text-[var(--gold)]' : 'text-white/60'} hover:text-[var(--gold)]`}><Pin className="w-3.5 h-3.5" /></button>
                <button data-testid={`content-hide-${c.id}`} onClick={() => toggleHide(c)} className="p-1.5 text-white/60 hover:text-[var(--gold)]">
                  {c.is_hidden ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                </button>
                <button data-testid={`content-delete-${c.id}`} onClick={() => setDeleteTarget(c)} className="p-1.5 text-white/60 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <AnimatePresence>
        {showEditor && (
          <ContentEditor
            initial={editing}
            defaultServer={server}
            onClose={() => setShowEditor(false)}
            onSaved={() => { setShowEditor(false); load(); }}
          />
        )}
        {previewItem && (
          <ContentPreview item={previewItem} onClose={() => setPreviewItem(null)} />
        )}
        {deleteTarget && (
          <ModalBg onClose={() => setDeleteTarget(null)}>
            <h3 className="font-heading text-lg">Elimina pubblicazione</h3>
            <p className="text-sm text-white/70">Vuoi eliminare definitivamente questa pubblicazione? L'azione non è reversibile.</p>
            <div className="flex gap-2">
              <button data-testid="content-delete-cancel" onClick={() => setDeleteTarget(null)} className="flex-1 py-3 rounded-full border border-[var(--border-subtle)] text-white/80 text-sm">Annulla</button>
              <button data-testid="content-delete-confirm" onClick={doDelete} className="flex-1 py-3 rounded-full bg-red-500 text-white font-semibold text-sm">Elimina</button>
            </div>
          </ModalBg>
        )}
      </AnimatePresence>
    </div>
  );
}

function ContentEditor({ initial, defaultServer, onClose, onSaved }) {
  const [server, setServer] = useState(initial?.server || defaultServer);
  const [text, setText] = useState(initial?.text || '');
  const [image, setImage] = useState(null);
  const [notify, setNotify] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const isEdit = !!initial;

  const submit = async () => {
    if (!text.trim()) return toast.error('Inserisci il testo');
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/contents/${initial.id}`, { text });
        toast.success('Pubblicazione aggiornata');
      } else {
        const form = new FormData();
        form.append('server', server);
        form.append('text', text);
        form.append('send_notification', String(notify));
        if (image) form.append('image', image);
        await api.post('/contents', form, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success('Pubblicato');
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore');
    } finally { setSaving(false); }
  };

  return (
    <ModalBg onClose={onClose} maxWidth="max-w-md">
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-lg font-medium">{isEdit ? 'Modifica pubblicazione' : 'Nuova pubblicazione'}</h3>
        <button onClick={onClose} className="text-white/50 hover:text-white p-1"><X className="w-4 h-4" /></button>
      </div>

      {!isEdit && (
        <div>
          <div className="text-[10px] uppercase tracking-widest text-white/50 mb-1">Server</div>
          <div className="flex gap-2">
            {SERVERS.map((s) => (
              <button
                key={s.id}
                data-testid={`editor-server-${s.id}`}
                onClick={() => setServer(s.id)}
                className={`flex-1 py-2.5 rounded-2xl text-sm flex items-center justify-center gap-2 ${
                  server === s.id
                    ? 'bg-[var(--bg-elevated)] border border-[var(--gold)]/40 text-white'
                    : 'bg-[var(--bg-surface)] border border-[var(--border-subtle)] text-white/60'
                }`}
              >
                <span className={`w-2 h-2 rounded-full ${s.dot}`} />
                {s.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {!isEdit && (
        <div>
          <div className="text-[10px] uppercase tracking-widest text-white/50 mb-1">Locandina (opzionale)</div>
          <button
            data-testid="editor-image-btn"
            onClick={() => fileRef.current?.click()}
            className="w-full py-4 rounded-2xl border border-dashed border-[var(--border-subtle)] hover:border-[var(--gold)]/50 text-sm text-white/60 flex items-center justify-center gap-2"
          >
            <ImageIcon className="w-4 h-4" />
            {image ? image.name : 'Scegli immagine…'}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => setImage(e.target.files?.[0] || null)}
          />
          {image && (
            <img
              src={URL.createObjectURL(image)}
              alt=""
              className="mt-2 w-full aspect-video object-cover rounded-xl"
            />
          )}
        </div>
      )}

      <div>
        <div className="text-[10px] uppercase tracking-widest text-white/50 mb-1">Testo (copia/incolla da Telegram)</div>
        <textarea
          data-testid="editor-text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          className="input-gold min-h-[220px] resize-y font-mono text-sm"
          placeholder="🎬 Nuovo film disponibile...&#10;&#10;⭐ Titolo · Anno · Genere&#10;📝 Descrizione..."
        />
      </div>

      {!isEdit && (
        <label className="flex items-center gap-3 cursor-pointer" data-testid="editor-notify-label">
          <input
            data-testid="editor-notify"
            type="checkbox"
            checked={notify}
            onChange={(e) => setNotify(e.target.checked)}
            className="w-4 h-4 accent-[var(--gold)]"
          />
          <div className="flex items-center gap-2 text-sm text-white/80">
            <Bell className="w-4 h-4 text-[var(--gold)]" />
            Invia notifica ai clienti
          </div>
        </label>
      )}

      <button
        data-testid="editor-submit"
        onClick={submit}
        disabled={saving}
        className="btn-gold w-full py-3 flex items-center justify-center gap-2"
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4" /> {isEdit ? 'Salva modifiche' : 'Pubblica'}</>}
      </button>
    </ModalBg>
  );
}

function ContentPreview({ item, onClose }) {
  const meta = SERVERS.find((s) => s.id === item.server) || SERVERS[0];
  const isNew = (() => {
    try { return (Date.now() - new Date(item.created_at).getTime()) / (1000 * 60 * 60) < 48; } catch { return false; }
  })();
  const dateStr = (() => {
    try {
      const d = new Date(item.created_at);
      return d.toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' }) + ' · ' +
        d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
  })();

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/80 backdrop-blur-sm p-0 sm:p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="admin-preview-modal"
    >
      <motion.div
        initial={{ y: 60, scale: 0.98 }}
        animate={{ y: 0, scale: 1 }}
        exit={{ y: 60, scale: 0.98 }}
        onClick={(e) => e.stopPropagation()}
        className="card-surface w-full max-w-md sm:my-8 overflow-hidden relative"
      >
        {/* Header with close button (floating over image if present) */}
        <div className="absolute top-3 right-3 z-10">
          <button
            data-testid="admin-preview-close"
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-black/60 backdrop-blur flex items-center justify-center text-white/90 hover:bg-black/80"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Anteprima label */}
        <div className="absolute top-3 left-3 z-10">
          <div className="bg-black/60 backdrop-blur px-3 py-1 rounded-full text-[10px] uppercase tracking-widest text-[var(--gold)]">
            Anteprima cliente
          </div>
        </div>

        {item.image_url && (
          <div className="relative w-full aspect-video bg-[var(--bg-elevated)] overflow-hidden">
            <img
              src={mediaUrl(item.image_url)}
              alt=""
              className="w-full h-full object-cover"
              data-testid="admin-preview-image"
            />
            {item.is_pinned && (
              <div className="absolute bottom-3 left-3 bg-black/60 backdrop-blur px-3 py-1.5 rounded-full flex items-center gap-1.5 text-xs text-[var(--gold)]">
                <Pin className="w-3.5 h-3.5" /> In evidenza
              </div>
            )}
            {isNew && (
              <div className="absolute bottom-3 right-3 bg-[var(--gold)] text-black text-xs font-bold px-3 py-1.5 rounded-full tracking-widest">
                NUOVO
              </div>
            )}
          </div>
        )}

        <div className={`px-5 pb-6 ${item.image_url ? 'pt-5' : 'pt-14'}`}>
          {!item.image_url && (
            <div className="flex items-center gap-2 mb-3">
              {item.is_pinned && <Pin className="w-3.5 h-3.5 text-[var(--gold)]" />}
              {isNew && <span className="bg-[var(--gold)] text-black text-[10px] font-bold px-2 py-0.5 rounded-full tracking-widest">NUOVO</span>}
            </div>
          )}
          <div className="flex items-center gap-2 mb-4">
            <div className={`w-2 h-2 rounded-full ${meta.dot}`} />
            <span className="text-[10px] uppercase tracking-[0.3em] text-white/50">{meta.label}</span>
            <span className="text-[10px] text-white/40 ml-auto">{dateStr}</span>
          </div>

          <div
            data-testid="admin-preview-text"
            className="text-[15px] leading-relaxed text-white whitespace-pre-wrap break-words"
          >
            {item.text}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

function ModalBg({ children, onClose, maxWidth = 'max-w-sm' }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-4 overflow-y-auto"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 40, scale: 0.98 }}
        animate={{ y: 0, scale: 1 }}
        exit={{ y: 40, scale: 0.98 }}
        onClick={(e) => e.stopPropagation()}
        className={`card-surface p-5 w-full ${maxWidth} space-y-4 my-8`}
      >
        {children}
      </motion.div>
    </motion.div>
  );
}
